import React, { useEffect, useState } from 'react'
import { AccordianComponent } from '../../../../components/accordian/Accordian.components'
import { IonRow, IonCol, IonList, useIonViewDidEnter, useIonViewDidLeave } from '@ionic/react'
import SingleSelect from '../../../../components/singleSelect/SingleSelect.component'
import { getRespiratorySelector } from '../selectors'
import { useDispatch, useSelector } from 'react-redux'
import { IOption } from '../../../../common/common.types'
import {
  saveRespiratory,
  setBreathSoundsLeft,
  setBreathSoundsRight,
  setOutcome,
  setPerformedBy,
  setRespExam,
  setRespiratoryEffort,
  setVia,
  setRFIO2,
  setO2,
  setFIO2AirTemp,
  setFIO2Humidity,
} from '../store/respiratory.actions'
import Comment from '../../../../components/comment/Comment.component'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import NumPadToggleBox from '../../../../components/numPadToggleBox/NumPadToggleBox.component'
import { useTranslation } from 'react-i18next'
import { getRespiratoryOptionsSelector } from '../../../options/selectors/respiratoryOptions.selector'
import { RespiratoryConstant } from '../store/respiratory.constant'
import MultiSelect from '../../../../components/multiSelect/MultiSelect.component'
import { hyphenDotKeys, numberPadKeysWithDecimal } from '../../../../components/numberPad/NumberPad.types'
import { getDatapointsConfigSelector } from '../../../configurations/selectors'
import Validation from '../../../../components/validation/Validation.component'
import NumberPad from '../../../../components/numberPad/NumberPad.component'
import useDeviceMetrics from '../../../../utilities/useDeviceMetrics'

const RespiratoryContent: React.FC<any> = () => {
  const respiratoryState = useSelector(getRespiratorySelector)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { isTablet } = useDeviceMetrics()
  const respOptions = useSelector(getRespiratoryOptionsSelector)
  const {
    breathSoundLeftOptions,
    breathSoundRightOptions,
    respEffortOptions,
    respViaOptions,
    respPerformByOptions,
    respOutcomeOptions,
    respExamOptions,
  } = respOptions

  const {
    cvBreathSoundsL,
    cvBreathSoundsR,
    cvBreathComments,
    cvRespEffort,
    cvVia,
    cvO2,
    rfio2,
    fio2AirTemp,
    fio2Humidity,
    respBy,
    respComments,
    respOutcome,
    respExam,
    fio2AirTempUnit,
  } = respiratoryState

  const dataPointsConfig = useSelector(getDatapointsConfigSelector)
  const { DP_BREATH_PICKLIST, DP_BREATH_COMMENTS } = dataPointsConfig
  const [respiratoryValidationFlag, setRespiratoryValidationFlag] = useState(true)
  const [showButtonWall] = useState(false)

  const save = () => {
    dispatch(saveRespiratory())
  }

  const setBreathSoundsLeftOnChange = (option: IOption) => {
    dispatch(setBreathSoundsLeft(option))
    save()
  }

  const setBreathSoundsRightOnChange = (option: IOption) => {
    dispatch(setBreathSoundsRight(option))
    save()
  }

  const setRespEffortOnChange = (option: IOption) => {
    dispatch(setRespiratoryEffort(option))
    save()
  }

  const setRespO2OnChange = (numbers: string[]) => {
    dispatch({
      type: RespiratoryConstant.SET_O2,
      cvO2: numbers.length ? numbers.join('') : null,
    })
    save()
  }

  const setRespRfio2OnChange = (numbers: string[]) => {
    dispatch({
      type: RespiratoryConstant.SET_RFIO2,
      rfio2: numbers.length ? numbers.join('') : null,
    })
    save()
  }

  const setFio2AirTempOnChange = (numbers: string[]) => {
    dispatch({
      type: RespiratoryConstant.SET_FIO2AirTemp,
      fio2AirTemp: numbers.length ? numbers.join('') : null,
    })
    save()
  }

  const setFio2HumidityOnChange = (numbers: string[]) => {
    dispatch({
      type: RespiratoryConstant.SET_FIO2Humidity,
      fio2Humidity: numbers.length ? numbers.join('') : null,
    })
    save()
  }

  const setViaOnChange = (option: IOption) => {
    dispatch(setVia(option))
    save()
  }

  const setPerformByOnChange = (option: IOption) => {
    dispatch(setPerformedBy(option))
    save()
  }

  const setOutcomeOnChange = (option: IOption) => {
    dispatch(setOutcome(option))
    save()
  }

  useEffect(() => {
    if (respiratoryValidationFlag) {
      cvO2 && dispatch(setO2(cvO2))
      rfio2 && dispatch(setRFIO2(cvO2))
      fio2AirTemp && dispatch(setFIO2AirTemp(fio2AirTemp))
      fio2Humidity && dispatch(setFIO2Humidity(fio2AirTemp))
    }
  }, [respiratoryValidationFlag])

  useIonViewDidEnter(() => {
    setRespiratoryValidationFlag(true)
  })

  useIonViewDidLeave(() => {
    setRespiratoryValidationFlag(false)
  })

  const breathSoundData = (
    <>
      {DP_BREATH_PICKLIST === 1 && breathSoundLeftOptions?.length > 0 && breathSoundRightOptions?.length > 0 && (
        <IonRow>
          <IonCol className='picklist' size='6'>
            <SingleSelect
              dataTestID='BreathSoundsLeft'
              options={breathSoundLeftOptions}
              selectedOption={cvBreathSoundsL}
              listHeader={t('respiratory.BreathSoundsLeft')}
              label={t('respiratory.BreathSoundsLeft')}
              action={setBreathSoundsLeftOnChange}
            />
            <hr className='field-divider' />
          </IonCol>

          <IonCol className='picklist' size='6'>
            <SingleSelect
              dataTestID='BreathSoundsRight'
              options={breathSoundRightOptions}
              selectedOption={cvBreathSoundsR}
              listHeader={t('respiratory.BreathSoundsRight')}
              label={t('respiratory.BreathSoundsRight')}
              action={setBreathSoundsRightOnChange}
            />
            <hr className='field-divider' />
          </IonCol>
        </IonRow>
      )}

      {DP_BREATH_COMMENTS === 1 && (
        <IonRow>
          <IonCol>
            <Comment
              isFieldComment={true}
              isDataPresent={cvBreathComments?.length > 0}
              path={TabConstants.INITIAL_ASSESSMENT_COMMENTS}
            />
          </IonCol>
        </IonRow>
      )}
    </>
  )

  const hasRespAnyData = Boolean(cvBreathSoundsL || cvBreathSoundsR || cvBreathComments)

  return (
    <div id='respiratoryContent'>
      <IonList className={isTablet ? 'ion-margin-horizontal' : ''}>
        {(DP_BREATH_PICKLIST === 1 || DP_BREATH_COMMENTS === 1) && (
          <AccordianComponent
            heading={t('respiratory.BreathSounds')}
            content={breathSoundData}
            isCheckMark={hasRespAnyData}
          />
        )}
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='RespEfforts'
              options={respEffortOptions}
              selectedOption={cvRespEffort}
              listHeader={t('respiratory.RespEfforts')}
              label={t('respiratory.RespEfforts')}
              action={setRespEffortOnChange}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        {!showButtonWall ? (
          <IonRow>
            <IonCol className='picklist'>
              <NumberPad
                isPHI
                className='resp-numberpad'
                elementId='cvO2'
                dataTestId='cvO2'
                values={cvO2 ? cvO2?.split('') : []}
                label={t('respiratory.RespO2')}
                validCharacters={numberPadKeysWithDecimal}
                onNumPadClick={setRespO2OnChange}
                action={RespiratoryConstant.SET_O2}
              />
              <Validation id={RespiratoryConstant.SET_O2} />
            </IonCol>
          </IonRow>
        ) : (
          <IonRow className='gray-bg button-align'>
            <NumPadToggleBox
              numberPadValues={cvO2 ? cvO2?.split('') : []}
              score={cvO2 || ''}
              action={setRespO2OnChange}
              label={t('respiratory.RespO2')}
              unitLabel={'l/m'}
              validCharacters={hyphenDotKeys}
              validationAction={RespiratoryConstant.SET_O2}
            />
            <NumPadToggleBox
              numberPadValues={rfio2 ? rfio2.toString().split('') : []}
              score={rfio2 || ''}
              action={setRespRfio2OnChange}
              label={t('respiratory.FiO2')}
              unitLabel={'%'}
              validCharacters={hyphenDotKeys}
              validationAction={RespiratoryConstant.SET_RFIO2}
            />
            <NumPadToggleBox
              numberPadValues={fio2AirTemp ? fio2AirTemp.toString().split('') : []}
              score={fio2AirTemp || ''}
              action={setFio2AirTempOnChange}
              label={t('respiratory.RespAirTemp')}
              unitLabel={fio2AirTempUnit === 'f' ? 'F°' : 'C°'}
              validCharacters={hyphenDotKeys}
              isTemp={true}
              dataTestId={'Fio2AirTemp_unit'}
              validationAction={RespiratoryConstant.SET_FIO2AirTemp}
            />
            <NumPadToggleBox
              numberPadValues={fio2Humidity ? fio2Humidity.toString().split('') : []}
              score={fio2Humidity || ''}
              action={setFio2HumidityOnChange}
              label={t('respiratory.RespHumidity')}
              unitLabel={'%'}
              validCharacters={hyphenDotKeys}
              validationAction={RespiratoryConstant.SET_FIO2Humidity}
            />
            <Validation id={RespiratoryConstant.SET_O2} />
            <Validation id={RespiratoryConstant.SET_RFIO2} />
            <Validation id={RespiratoryConstant.SET_FIO2AirTemp} />
            <Validation id={RespiratoryConstant.SET_FIO2Humidity} />
          </IonRow>
        )}
        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='Via'
              options={respViaOptions}
              selectedOption={cvVia}
              listHeader={t('respiratory.cvVia')}
              label={t('respiratory.cvVia')}
              action={setViaOnChange}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />

        <IonRow>
          <IonCol className='picklist ion-no-padding' size='6'>
            <SingleSelect
              dataTestID='PerformBy'
              options={respPerformByOptions}
              selectedOption={respBy}
              listHeader={t('respiratory.RespPerformedBy')}
              label={t('respiratory.RespPerformedBy')}
              action={setPerformByOnChange}
            />
            <hr className='field-divider' />
          </IonCol>

          <IonCol className='picklist ion-no-padding' size='6'>
            <SingleSelect
              dataTestID='Outcome'
              options={respOutcomeOptions}
              selectedOption={respOutcome}
              listHeader={t('respiratory.RespOutcome')}
              label={t('respiratory.RespOutcome')}
              action={setOutcomeOnChange}
            />
            <hr className='field-divider' />
          </IonCol>
        </IonRow>

        {respExamOptions && respExamOptions?.length > 0 && (
          <>
            <IonRow>
              <IonCol className='picklist ion-no-margin'>
                <MultiSelect
                  dataTestID='RespiratoryExam'
                  label={t('respiratory.RespExam')}
                  listHeader={t('respiratory.RespExam')}
                  options={respExamOptions}
                  savedValues={respExam}
                  action={setRespExam}
                  showChips={true}
                  callback={saveRespiratory}
                />
              </IonCol>
            </IonRow>

            <hr className='field-divider' />
          </>
        )}

        <IonRow>
          <IonCol className='ion-no-padding'>
            <Comment
              isFieldComment={false}
              isDataPresent={respComments?.length > 0}
              path={TabConstants.INITIAL_ASSESSMENT_COMMENTS}
            />
          </IonCol>
        </IonRow>
      </IonList>
    </div>
  )
}

export default RespiratoryContent
